
import { ref, computed, useContext, useRoute, watch, nextTick, onMounted, onBeforeUnmount } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';
import { useCartStore } from '~/stores/useCartStore';
import { useAppliedDiscountCodeStore } from '~/stores/voucher/useAppliedDiscountCodeStore';
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore';
import { ACTION_STATUS } from '~/constants/ActionStatus';

// Import composables
import { useVoucherApplication } from '~/composable/voucher/useVoucherApplication';
import { useVoucherWarning } from '~/composable/voucher/useVoucherWarning';
import { useVoucherCartState } from '~/composable/voucher/useVoucherCartState';
import { useVoucherInstallmentOptions } from '~/composable/voucher/useVoucherInstallmentOptions';
import { useVoucherErrorHandling } from '~/composable/voucher/useVoucherErrorHandling';
import { useVoucherHelpers } from '~/composable/voucher/useVoucherHelpers';
import { useVoucherTracking } from '~/composable/voucher/useVoucherTracking';
import { voucherDebug, voucherDebugWarn, voucherDebugError } from '~/composable/voucher/useVoucherDebug';
import useUtilities from '~/composable/useUtilities';
import useCategories from '~/composable/useCategories';
import useTlmCart from '~/composable/useTlmCart';
import { isPercentageVoucherDiscount } from '~/composable/voucher/resolveDevicePriceForCart';

export default {
  name: 'ProductDetailsVoucherCode',
  emits: ['applied', 'error', 'reapplying'],
  setup(_, { emit }) {
    const { $hubble } = useContext();
    const route = useRoute();
    const cartStore = useCartStore();
    const appliedDiscountCodeStore = useAppliedDiscountCodeStore();
    const chosenVariantDetailsStore = useChosenVariantDetailsStore();
    const { getCart } = storeToRefs(cartStore);
    const { productDetails, chosenChannel, chosenInstallment, chosenStorage, chosenColor } = storeToRefs(chosenVariantDetailsStore);
    const { hasAppliedDiscountCode } = storeToRefs(appliedDiscountCodeStore);
    const { formatPrice, formatPriceWithDecimals } = useUtilities();

    // Initialize composables
    const context = useContext();
    const { app } = context;
    const { getCategory } = useCategories($hubble);
    const { persistCategoryIdOnCart } = useTlmCart(app);
    const { getCartID, getProductInfo: getProductInfoHelper } = useVoucherHelpers();
    const { isCartStateError, isCartStateErrorInResponse, getErrorMessage: getErrorMessageHelper } = useVoucherErrorHandling();
    const voucherApp = useVoucherApplication(context);
    const voucherWarning = useVoucherWarning();
    const voucherCartState = useVoucherCartState();
    const installmentOptions = useVoucherInstallmentOptions();
    const { trackApplyVoucher, trackApplyVoucherError, trackRemoveVoucher, trackReapplyVoucher } = useVoucherTracking(context);

    // Use composable state instead of local refs
    const { loading, removing, errorMessage, infoMessage, isApplied } = voucherApp;

    // Check if current product is device or accessory
    const isDeviceOrAccessory = computed(() => {
      const categorySlug = route.value?.params?.categoryslug?.toLowerCase();
      const isAccessoryCategory = categorySlug === 'tilbehoer';
      const isMobileCategory = categorySlug === 'mobiltelefoner';
      const productType = productDetails.value?.chosenVariant?.productTypeKey;
      return productType === 'device' || productType === 'accessory' || isAccessoryCategory || isMobileCategory;
    });

    // Detect keep-subscription carts so voucher restore/reapply still works
    const isKeepSubscriptionCart = computed(() => {
      const cart = getCart.value;
      return !!cart && !!cart.keepSubscriptionType;
    });

    // Local state (still needed)
    const voucherCode = ref('');
    const panel = ref([]);

    // Store original installment options prices before any discount is applied
    const originalInstallmentOptions = ref(null);
    // Flag to prevent watchers from triggering reapply during cart update
    const isUpdatingFromCart = ref(false);
    // Flag to prevent handleCartChange from overwriting discount amount during reapply
    const isReapplyingVoucher = ref(false);
    // Flag to prevent voucher restoration during product change
    const isChangingProduct = ref(false);
    // Block cart/store restore until PDP entry reset completes (mount) or while leaving PDP (unmount)
    const suppressVoucherRestoreFromCart = ref(true);

    // Store original installment options when product/channel is first loaded
    const storeOriginalInstallmentOptions = () => {
      const originals = installmentOptions.storeOriginalInstallmentOptions();
      if (originals) {
        originalInstallmentOptions.value = originals;
        voucherDebug('Stored original installment options:', originalInstallmentOptions.value);
      }
    };

    // Wait for productDetails to be updated with new product's data
    const waitForProductDetailsUpdate = async (expectedProductSlug = null) => {
      const maxRetries = 10;
      const initialProductName = productDetails.value?.productName;

      const checkSlugMatch = (currentSlug) => {
        return !expectedProductSlug || !currentSlug || currentSlug === expectedProductSlug;
      };

      const checkProductNameMatch = (currentName) => {
        if (!expectedProductSlug || !currentName) {
          return true;
        }
        const slugProductName = expectedProductSlug.split('-').slice(-1)[0].toLowerCase();
        return currentName.toLowerCase().includes(slugProductName);
      };

      const isProductDetailsReady = () => {
        const hasPriceMatrix = !!productDetails.value?.chosenVariantPriceMatrix;
        const currentProductSlug = route.value?.params?.productslug;
        const currentProductName = productDetails.value?.productName;
        return hasPriceMatrix && checkSlugMatch(currentProductSlug) && checkProductNameMatch(currentProductName);
      };

      const checkReady = () => {
        if (!isProductDetailsReady()) {
          return false;
        }
        const currentProductName = productDetails.value?.productName;
        const productChanged = initialProductName && currentProductName && currentProductName !== initialProductName;
        return !(expectedProductSlug && !productChanged);
      };

      if (checkReady()) {
        return true;
      }

      for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
        await nextTick();
        await new Promise(resolve => setTimeout(resolve, 50));
        if (checkReady()) {
          return true;
        }
      }

      if (productDetails.value?.chosenVariantPriceMatrix) {
        voucherDebugWarn('ProductDetails partially ready, proceeding with reset');
        return true;
      }
      voucherDebugWarn('ProductDetails still not ready after', maxRetries, 'retries');
      return false;
    };

    const waitForChannelOptions = async () => {
      voucherDebug('No originals or chosenChannel not ready, waiting for InstallmentsOptions to reset...');
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 50));
    };

    const storeOriginalsIfAvailable = () => {
      if (!originalInstallmentOptions.value && chosenChannel.value?.installmentOptions) {
        storeOriginalInstallmentOptions();
      }
    };

    // Validate and prepare original installment options
    const validateAndPrepareOriginals = async () => {
      const hasOriginals = !!originalInstallmentOptions.value;
      const hasChannelOptions = !!chosenChannel.value?.installmentOptions;
      if (hasOriginals && hasChannelOptions) {
        return true;
      }
      await waitForChannelOptions();
      storeOriginalsIfAvailable();
      if (!chosenChannel.value?.installmentOptions) {
        voucherDebugWarn('chosenChannel still not ready after waiting');
        return false;
      }
      return true;
    };

    // Ensure originals match current product
    const ensureOriginalsMatch = () => {
      if (!originalInstallmentOptions.value || !chosenChannel.value?.installmentOptions) {
        return false;
      }
      if (originalInstallmentOptions.value.length !== chosenChannel.value.installmentOptions.length) {
        voucherDebug('Originals do not match current product, storing fresh originals from chosenChannel');
        storeOriginalInstallmentOptions();
        if (originalInstallmentOptions.value.length !== chosenChannel.value.installmentOptions.length) {
          voucherDebugWarn('Cannot reset - originals still do not match after storing fresh ones');
          return false;
        }
      }
      return true;
    };

    const resetInstallmentOptionsToOriginal = async () => {
      const expectedProductSlug = route.value?.params?.productslug;
      await waitForProductDetailsUpdate(expectedProductSlug);

      const isReady = await validateAndPrepareOriginals();
      if (!isReady) {
        return;
      }

      const isValid = ensureOriginalsMatch();
      if (!isValid) {
        return;
      }

      if (originalInstallmentOptions.value) {
        await installmentOptions.resetInstallmentOptionsToOriginal(originalInstallmentOptions.value, route);
        installmentOptions.updateChosenInstallmentFromOptions();
        voucherDebug('Reset installment options to original prices');
      }
    };

    // PDP should never restore voucher state from cart when (re)entering the page
    const isComingBackFromCheckout = () => false;

    // Use composable warning computed
    const showDiscountExceedsPriceWarning = voucherWarning.showDiscountExceedsPriceWarning(isApplied)

    const resetVoucherState = () => {
      voucherCode.value = '';
      isApplied.value = false;
      errorMessage.value = '';
      infoMessage.value = '';
      // Collapse panel when voucher is removed
      panel.value = [];

      if (reapplyTimer) {
        clearTimeout(reapplyTimer);
        reapplyTimer = null;
      }
    };

    const clearError = () => {
      try {
        errorMessage.value = '';
        isApplied.value = false;
        appliedDiscountCodeStore.clearDiscountCodes();
      } catch (err) {
        voucherDebugError('Error in clearError:', err);
      }
    };

    const resetAppliedDiscountStateKeepingCode = () => {
      const codeToKeep = voucherCode.value;
      isApplied.value = false;
      appliedDiscountCodeStore.clearDiscountCodes();
      if (codeToKeep) {
        appliedDiscountCodeStore.setEnteredVoucherCode(codeToKeep);
      }
    };

    const hasCartDiscountCodes = (cart) => {
      return cart.appliedDiscountCodes && cart.appliedDiscountCodes.length > 0;
    };

    const isCartValidForCurrentProduct = (cart) => {
      if (!cartContainsCurrentProduct(cart)) {
        voucherDebug('Cart does not contain current product');
        appliedDiscountCodeStore.setEnteredVoucherCode('');
        return false;
      }
      return true;
    };

    const isCartValidForDiscountCodes = (cart, actionLabel) => {
      if (!hasCartDiscountCodes(cart)) {
        voucherDebug(`Cannot ${actionLabel}: cart has no applied discount codes`);
        appliedDiscountCodeStore.setEnteredVoucherCode('');
        return false;
      }
      return true;
    };

    // Validate cart before attempting restore
    const validateCartForAttemptRestore = (cart) => {
      if (!cart || !cartContainsCurrentProduct(cart)) {
        voucherDebug('Cannot attempt restore: cart does not contain current product');
        appliedDiscountCodeStore.setEnteredVoucherCode('');
        return false;
      }

      return isCartValidForDiscountCodes(cart, 'attempt restore');
    };

    // Helper function to attempt restore from different sources (using composable)
    const attemptRestore = () => {
      const cart = getCart.value;
      if (!validateCartForAttemptRestore(cart)) {
        return false;
      }

      const wasRestored = voucherCartState.attemptRestore(
        voucherCode,
        isApplied,
        (code) => { voucherCode.value = code },
        (applied) => { isApplied.value = applied }
      );

      // CRITICAL: If restore failed, clear enteredVoucherCode to prevent it from being restored later
      if (!wasRestored) {
        appliedDiscountCodeStore.setEnteredVoucherCode('');
      }

      return wasRestored;
    };

    // Helper to restore code from cart code (for reapply logic)
    const restoreCodeFromCartCode = (cartCode) => {
      return voucherCartState.restoreCodeFromCartCode(
        cartCode,
        voucherCode,
        isApplied,
        (code) => { voucherCode.value = code },
        (applied) => { isApplied.value = applied }
      )
    };

    // Helper function to reapply voucher after restore if coming from checkout
    const reapplyAfterRestoreIfNeeded = async (wasRestored) => {
      if (!wasRestored || !isComingBackFromCheckout() || !isApplied.value || !voucherCode.value) {
        return;
      }
      voucherDebug('Voucher code restored from cart, will reapply:', voucherCode.value);
      await nextTick();
      debouncedReapply(300, 'restore from cart');
    };

    // Validate cart before restoring voucher code
    const validateCartForRestore = (cart) => {
      if (!cart) {
        return false;
      }
      if (!isCartValidForCurrentProduct(cart)) {
        voucherDebug('Cannot restore voucher code: cart does not contain current product');
        return false;
      }
      return isCartValidForDiscountCodes(cart, 'restore voucher code');
    };

    const restoreVoucherCodeFromCart = async () => {
      try {
        const cart = getCart.value;
        if (!validateCartForRestore(cart)) {
          return;
        }

        // First sync store with cart to ensure we have latest data
        // Don't clear voucher codes when restoring (not a page load)
        await syncStoreWithCart(false);

        const wasRestored = attemptRestore();
        await reapplyAfterRestoreIfNeeded(wasRestored);
      } catch (err) {
        voucherDebugError('Error restoring voucher code:', err);
      }
    };

    // Use composable helpers for cart operations
    const findDeviceItemInCart = (cart) => voucherCartState.findDeviceItemInCart(cart);
    const getDiscountedPriceFromCart = (cart) => voucherCartState.getDiscountedPriceFromCart(cart);
    const getSubscriptionMonthlyPrice = (cart) => installmentOptions.getSubscriptionMonthlyPrice(cart);
    const getMonthlyAmountWithSubscriptionFromCart = (cart) => installmentOptions.getMonthlyAmountWithSubscriptionFromCart(cart);
    const updateChosenInstallmentFromOptions = () => installmentOptions.updateChosenInstallmentFromOptions();
    const updateAllInstallmentOptions = (discountedPrice, subscriptionMonthlyPrice, cart = null) => {
      installmentOptions.updateAllInstallmentOptions(discountedPrice, subscriptionMonthlyPrice, cart);
    };
    const createUpdatedCartItem = (deviceItem, updatedInstallment) => voucherCartState.createUpdatedCartItem(deviceItem, updatedInstallment);
    const updateCartItemInGroup = (cart, deviceItem, updatedCartItem) => voucherCartState.updateCartItemInGroup(cart, deviceItem, updatedCartItem);
    const getCartInstallmentMatchingOption = (cart) => installmentOptions.getCartInstallmentMatchingOption(cart);
    const recalculateCartInstallmentMonthlyAmount = (matchingOption, cart) => installmentOptions.recalculateCartInstallmentMonthlyAmount(matchingOption, cart);
    const syncMinimumPriceToCart = (deviceItem) => voucherCartState.syncMinimumPriceToCart(deviceItem);
    const ensureCartInstallmentHasMonthlyAmount = (cart, discountedPrice, subscriptionMonthlyPrice) => {
      installmentOptions.ensureCartInstallmentHasMonthlyAmount(cart, discountedPrice, subscriptionMonthlyPrice);
    };

    const updateCartInstallmentFromChosenChannel = (cart) => {
      const installmentData = getCartInstallmentMatchingOption(cart);
      if (!installmentData) {
        return;
      }
      const { deviceItem, matchingOption } = installmentData;
      const recalculatedMonthlyAmount = recalculateCartInstallmentMonthlyAmount(matchingOption, cart);
      syncMinimumPriceToCart(deviceItem);
      const updatedInstallment = {
        ...deviceItem.cartProduct.selectedPrice.selectedInstallment,
        amount: matchingOption.amount,
        monthlyAmountWithSubscription: recalculatedMonthlyAmount
      };
      const updatedCartItem = createUpdatedCartItem(deviceItem, updatedInstallment);
      updateCartItemInGroup(cart, deviceItem, updatedCartItem);
    }

    const shouldSkipUpdateInstallmentOptionsFromCart = (cart) => {
      if (!chosenChannel.value) {
        return true
      }
      const discountedPrice = getDiscountedPriceFromCart(cart)
      if (!discountedPrice) {
        return true
      }
      return false
    }

    const logInstallmentOptionsFromCart = (cart, discountedPrice, subscriptionMonthlyPrice) => {
      voucherDebug('Updated all installment options from cart:', {
        discountedPrice,
        subscriptionMonthlyPrice,
        bundlePrice: chosenChannel.value?.productBundlePrice?.bundlePrice,
        cartMonthlyAmount: getMonthlyAmountWithSubscriptionFromCart(cart),
        currentMonths: chosenInstallment.value?.months,
        installmentOptions: (chosenChannel.value?.installmentOptions || []).map(opt => ({
          months: opt.months,
          amount: opt.amount,
          monthlyAmountWithSubscription: opt.monthlyAmountWithSubscription
        })),
        chosenInstallment: {
          months: chosenInstallment.value?.months,
          amount: chosenInstallment.value?.amount,
          monthlyAmountWithSubscription: chosenInstallment.value?.monthlyAmountWithSubscription
        }
      })
    }

    const logAfterUpdateChosenInstallment = () => {
      voucherDebug('updateInstallmentOptionsFromCart - After updateChosenInstallmentFromOptions:', {
        chosenInstallment: {
          months: chosenInstallment.value?.months,
          amount: chosenInstallment.value?.amount,
          monthlyAmountWithSubscription: chosenInstallment.value?.monthlyAmountWithSubscription
        },
        matchingOption: (chosenChannel.value?.installmentOptions || []).find(
          opt => opt.months === chosenInstallment.value?.months
        )
      })
    }

    const logFinalUpdateAfterFlagReset = () => {
      voucherDebug('updateInstallmentOptionsFromCart - Final update after flag reset:', {
        chosenInstallment: {
          months: chosenInstallment.value?.months,
          amount: chosenInstallment.value?.amount,
          monthlyAmountWithSubscription: chosenInstallment.value?.monthlyAmountWithSubscription
        },
        allOptions: (chosenChannel.value?.installmentOptions || []).map(opt => ({
          months: opt.months,
          amount: opt.amount,
          monthlyAmountWithSubscription: opt.monthlyAmountWithSubscription
        }))
      })
    }

    const updateInstallmentOptionsFromCart = async (cart) => {
      if (shouldSkipUpdateInstallmentOptionsFromCart(cart)) {
        return
      }

      const discountedPrice = getDiscountedPriceFromCart(cart)

      // Set flag to prevent watchers from triggering reapply during cart update
      isUpdatingFromCart.value = true

      try {
        const subscriptionMonthlyPrice = getSubscriptionMonthlyPrice(cart)

        voucherDebug('updateInstallmentOptionsFromCart - values:', {
          discountedPrice,
          subscriptionMonthlyPrice,
          bundlePrice: chosenChannel.value?.productBundlePrice?.bundlePrice,
          currentMonths: chosenInstallment.value?.months,
          currentAmount: chosenInstallment.value?.amount,
          currentMonthlyAmount: chosenInstallment.value?.monthlyAmountWithSubscription
        })

        // First, ensure cart's selectedInstallment has monthlyAmountWithSubscription calculated
        ensureCartInstallmentHasMonthlyAmount(cart, discountedPrice, subscriptionMonthlyPrice)

        // Pass cart to updateAllInstallmentOptions so it can use cart's monthlyAmountWithSubscription
        updateAllInstallmentOptions(discountedPrice, subscriptionMonthlyPrice, cart)

        logInstallmentOptionsFromCart(cart, discountedPrice, subscriptionMonthlyPrice)

        // CRITICAL: Ensure chosenInstallment is updated AFTER all options are updated
        updateChosenInstallmentFromOptions()

        logAfterUpdateChosenInstallment()
      } finally {
        // Reset flag after updates are complete and ensure final update
        isUpdatingFromCart.value = false
        await nextTick()

        // CRITICAL: Call again after flag is reset to ensure reactivity picks up the changes
        updateChosenInstallmentFromOptions()

        logFinalUpdateAfterFlagReset()
      }
    }

    // Use composable methods
    const updateDiscountCodeStore = (updatedCart) => voucherCartState.updateDiscountCodeStore(updatedCart, voucherCode.value);
    const waitForWatchersToProcess = async () => await voucherCartState.waitForWatchersToProcess();

    const updateCartInstallmentAfterStoreUpdate = () => {
      // CRITICAL: Update cart's selectedInstallment again after cart is set in store and all watchers have processed
      // This ensures checkout page shows the correct "Samlet pris pr. md." (subscription + recalculated installment)
      // Get the cart from store to ensure we're updating the same object that checkout page reads from
      // NOTE: useTlmCart.js now always recalculates monthlyAmountWithSubscription correctly,
      // but we still update here to ensure it's set immediately before checkout page reads it
      const cartFromStore = getCart.value
      if (!cartFromStore) {
        return
      }

      const deviceItem = findDeviceItemInCart(cartFromStore)
      const selectedInstallment = deviceItem?.cartProduct?.selectedPrice?.selectedInstallment
      if (selectedInstallment) {
        const originalMonthlyAmount = selectedInstallment.monthlyAmountWithSubscription

        // Update with our correctly recalculated value
        updateCartInstallmentFromChosenChannel(cartFromStore)

        // Verify the update worked
        voucherDebug('Cart installment update verification:', {
          before: originalMonthlyAmount,
          after: selectedInstallment.monthlyAmountWithSubscription,
          months: selectedInstallment.months
        })
      }

      // CRITICAL: Create a deep clone to ensure Vue reactivity and that checkout page sees updated values
      // This ensures checkout/kvittering page shows the correct voucher-adjusted installment prices
      const clonedCart = JSON.parse(JSON.stringify(cartFromStore))
      cartStore.setCart(clonedCart)
      if (selectedInstallment) {
        voucherDebug('Cart updated in store with voucher-adjusted installment prices for checkout:', {
          months: selectedInstallment.months,
          amount: selectedInstallment.amount,
          monthlyAmountWithSubscription: selectedInstallment.monthlyAmountWithSubscription
        })
      }
    }


    const ensureCartSelectedInstallmentFromChosen = (cart) => {
      const deviceItem = findDeviceItemInCart(cart)
      if (!deviceItem?.cartProduct?.selectedPrice) {
        return
      }
      if (deviceItem.cartProduct.selectedPrice.selectedInstallment) {
        return
      }
      const months = chosenInstallment.value?.months
      if (months === undefined) {
        return
      }
      const matchingOption = chosenChannel.value?.installmentOptions?.find(
        opt => opt.months === months
      )
      if (!matchingOption) {
        return
      }
      const updatedInstallment = {
        months,
        amount: matchingOption.amount,
        monthlyAmountWithSubscription: matchingOption.monthlyAmountWithSubscription
      }
      const updatedCartItem = createUpdatedCartItem(deviceItem, updatedInstallment)
      updateCartItemInGroup(cart, deviceItem, updatedCartItem)
    }

    const processCartUpdates = async (updatedCart) => {
      ensureCartSelectedInstallmentFromChosen(updatedCart)

      // Update installment options from cart to reflect discount code changes
      // This must be done BEFORE setting cart in store, so cart is updated with new installment amounts
      // NOTE: updateInstallmentOptionsFromCart already calls updateChosenInstallmentFromOptions internally
      await updateInstallmentOptionsFromCart(updatedCart);

      // CRITICAL: Update cart's selectedInstallment with recalculated monthlyAmountWithSubscription
      // This ensures "Samlet pris pr. md." on checkout page shows the correct value
      // Must be done AFTER updateAllInstallmentOptions so matchingOption has recalculated values
      updateCartInstallmentFromChosenChannel(updatedCart);
    }

    // Use composable methods
    const updateCartInStore = (updatedCart) => voucherCartState.updateCartInStore(updatedCart);
    const waitForReactiveUpdates = async () => await voucherCartState.waitForReactiveUpdates();

    const persistPdpCategoryIdOnCart = async (updatedCart) => {
      const categorySlug = route.value?.params?.categoryslug
      if (!categorySlug || !updatedCart?.cartItemGroupList?.length) {
        return
      }
      const category = await getCategory({ categorySlug })
      persistCategoryIdOnCart(updatedCart, category?.id)
    }

    const finalizeInstallmentUpdates = async () => {
      // CRITICAL: Update cart installment again after all updates to ensure checkout/kvittering page shows correct prices
      updateCartInstallmentAfterStoreUpdate()

      // CRITICAL: Ensure chosenInstallment is updated with recalculated monthlyAmountWithSubscription
      // This ensures "Pris i alt pr. måned" displays the correct discounted price immediately
      // Call again after nextTick to ensure reactivity has processed
      updateChosenInstallmentFromOptions()

      await waitForWatchersToProcess()

      // Update again after InstallmentsOptions.vue has potentially overwritten our values
      // This ensures we use the correct calculation: monthlyAmountWithSubscription = subscriptionPrice + installmentAmount
      updateChosenInstallmentFromOptions()

      updateCartInstallmentAfterStoreUpdate()

      await nextTick() // One more tick to ensure UI updates
    }

    // Helper function to get applied discount code from cart
    const getAppliedDiscountCode = (updatedCart) => {
      if (!updatedCart?.appliedDiscountCodes || updatedCart.appliedDiscountCodes.length === 0) {
        return null;
      }
      return updatedCart.appliedDiscountCodes[0];
    };

    const hasMinimumPriceConfigured = (minimumPrice) => {
      return typeof minimumPrice === 'number' && minimumPrice > 0;
    };

    const checkPriceBelowMinimum = (discountedPrice, minimumPrice, cart) => {
      if (discountedPrice === null || !hasMinimumPriceConfigured(minimumPrice)) {
        return null;
      }
      if (discountedPrice < minimumPrice) {
        const formattedMinimum = isPercentageVoucherDiscount(cart)
          ? formatPriceWithDecimals(minimumPrice)
          : formatPrice(minimumPrice);
        return `Mindstebeløbet efter rabat for denne rabatkode er ${formattedMinimum} kr.`;
      }
      return null;
    };

    const validateMinimumPrice = (updatedCart) => {
      const appliedCode = getAppliedDiscountCode(updatedCart);
      if (!appliedCode) {
        return null;
      }

      // Use ONLY the minimum price configured on the discount code custom field.
      // Do NOT fall back to product/bundle minimum prices, as they represent
      // different business rules.
      const minimumPrice = appliedCode.minimumPrice;
      if (!hasMinimumPriceConfigured(minimumPrice)) {
        return null;
      }

      const discountedPrice = getDiscountedPriceFromCart(updatedCart);
      return checkPriceBelowMinimum(discountedPrice, minimumPrice, updatedCart);
    };

    const handleSuccess = async (updatedCart) => {
      try {
        isApplied.value = true;
        errorMessage.value = '';
        if (!updatedCart) {
          emit('applied', { code: voucherCode.value, cart: updatedCart });
          return
        }

        // Validate minimum price before proceeding (PHASE 2: requires custom field setup in CommerceTools)
        const minimumPriceError = validateMinimumPrice(updatedCart);
        if (minimumPriceError) {
          resetAppliedDiscountStateKeepingCode();
          errorMessage.value = minimumPriceError;
          // Track error
          trackApplyVoucherError({
            voucherCode: voucherCode.value,
            errorMessage: minimumPriceError,
            errorCode: 'MINIMUM_PRICE_ERROR'
          });
          emit('error', minimumPriceError);
          return;
        }

        updateDiscountCodeStore(updatedCart);
        await processCartUpdates(updatedCart);
        updateCartInStore(updatedCart);
        await persistPdpCategoryIdOnCart(updatedCart);
        await waitForReactiveUpdates();
        await finalizeInstallmentUpdates();

        // Track successful voucher application
        trackApplyVoucher({
          voucherCode: voucherCode.value,
          updatedCart
        });

        emit('applied', { code: voucherCode.value, cart: updatedCart });
      } catch (err) {
        voucherDebugError('Error in handleSuccess:', err);
      }
    };

    const handleError = (message) => {
      try {
        errorMessage.value = message;
        // Track voucher application error
        trackApplyVoucherError({
          voucherCode: voucherCode.value,
          errorMessage: message
        });
        resetAppliedDiscountStateKeepingCode();
        emit('error', message);
      } catch (err) {
        voucherDebugError('Error in handleError:', err);
        errorMessage.value = message;
        isApplied.value = false;
      }
    };


    // Apply voucher using composable
    const applyVoucher = async () => {
      await voucherApp.applyVoucher(
        voucherCode.value,
        handleSuccess,
        handleError
      )
    };

    const hasVoucherCodeInState = () => {
      return voucherCode.value || (getCart.value?.appliedDiscountCodes && getCart.value.appliedDiscountCodes.length > 0);
    };

    const isVoucherAppliedInState = () => {
      return isApplied.value || hasAppliedDiscountCode.value || (getCart.value?.appliedDiscountCodes && getCart.value.appliedDiscountCodes.length > 0);
    };

    const canReapplyVoucher = () => {
      const hasProductInfo = !!productDetails.value?.chosenVariantSku;
      const hasVoucherCode = hasVoucherCodeInState();
      const isVoucherApplied = isVoucherAppliedInState();
      const hasCartID = !!getCartID();
      return hasVoucherCode && isVoucherApplied && hasCartID && hasProductInfo;
    };

    const stopLoading = () => {
      infoMessage.value = '';
      loading.value = false;
    };

    const handleReapplySuccess = async (updatedCart) => {
      voucherDebug('Reapply response received:', {
        totalDiscountCodeDiscount: updatedCart.totalDiscountCodeDiscount,
        chosenChannelKey: chosenChannel.value?.channelKey,
        isCash: chosenChannel.value?.channelKey === 'Cash'
      });
      isReapplyingVoucher.value = true;
      // CRITICAL: Update discount code store IMMEDIATELY from backend response
      if (updatedCart?.totalDiscountCodeDiscount !== undefined) {
        appliedDiscountCodeStore.setTotalDiscountCodeDiscount(updatedCart.totalDiscountCodeDiscount);
      }
      if (updatedCart?.appliedDiscountCodes) {
        appliedDiscountCodeStore.setAppliedDiscountCodes(updatedCart.appliedDiscountCodes);
      }
      await updateInstallmentOptionsFromCart(updatedCart);
      cartStore.setCart(JSON.parse(JSON.stringify(updatedCart)));
      await persistPdpCategoryIdOnCart(updatedCart);
      await nextTick();
      updateChosenInstallmentFromOptions();
      await nextTick();
      isReapplyingVoucher.value = false;
      voucherDebug('Reapply completed:', {
        totalDiscountCodeDiscount: appliedDiscountCodeStore.totalDiscountCodeDiscount,
        chosenChannelKey: chosenChannel.value?.channelKey
      });
      // Track voucher reapply - note: reason is tracked when reapply is triggered
      emit('applied', { code: voucherCode.value, cart: updatedCart, reapplied: true });
    }



    const getErrorMessage = getErrorMessageHelper;

    const executeReapplyRequest = async () => {
      const { sku, channelKey, installmentPeriod } = getProductInfo();
      if (!sku) {
        return null;
      }
      const request = { discountCode: voucherCode.value, sku, channelKey, installmentPeriod };
      voucherDebug('Reapplying voucher with:', request, {
        chosenChannelKey: chosenChannel.value?.channelKey,
        chosenChannelRefId: chosenChannel.value?.channelReferenceID,
        chosenInstallmentMonths: chosenInstallment.value?.months,
        isCash: chosenChannel.value?.channelKey === 'Cash'
      });
      return await $hubble.cart.applyDiscountCodeWithProduct(getCartID(), request);
    };

    const shouldRetryReapply = (retryCount) => {
      return retryCount === 0 && voucherCode.value && isApplied.value && getCartID();
    };

    const handleReapplyRetry = (retryCount, reason) => {
      if (shouldRetryReapply(retryCount)) {
        voucherDebug(`Retrying reapply after delay (${reason})...`);
        setTimeout(() => reapplyVoucher(1), 500);
      }
    };


    const processReapplyResponse = async (response) => {
      const data = response?.data;
      if (data?.actionStatus === ACTION_STATUS.OK && data?.responseObject) {
        await handleReapplySuccess(data.responseObject);
      } else {
        isReapplyingVoucher.value = false;
        const actionError = data?.actionErrors?.[0];

        if (isCartStateErrorInResponse(data)) {
          cartStore.setCart(null);
          handleError('Indkøbskurven er ikke længere aktiv. Prøv venligst igen.');
        } else {
          const errorMsg = actionError?.notificationMessage || 'Rabatkoden kunne ikke genberegnes';
          handleError(errorMsg);
        }
      }
    };

    const handleReapplyError = (error, retryCount) => {
      voucherDebugError('Reapply voucher error:', error);
      stopLoading();

      if (isCartStateError(error)) {
        cartStore.setCart(null);
      }

      if (retryCount === 0) {
        handleReapplyRetry(retryCount, 'error occurred');
      } else {
        handleError(getErrorMessage(error));
      }
    };

    // Re-apply voucher code when variant options change
    const reapplyVoucher = async (retryCount = 0, reason = 'unknown') => {
      if (!canReapplyVoucher()) {
        voucherDebug('Cannot reapply voucher:', {
          hasCode: !!voucherCode.value,
          isApplied: isApplied.value,
          hasCartID: !!getCartID(),
          hasSku: !!productDetails.value?.chosenVariantSku
        });
        handleReapplyRetry(retryCount, 'product info not ready');
        return;
      }

      // Track reapply attempt (only on first attempt, not retries)
      if (retryCount === 0) {
        trackReapplyVoucher({
          voucherCode: voucherCode.value,
          reason
        });
      }

      try {
        emit('reapplying');
        loading.value = true;
        infoMessage.value = 'Genberegner rabatkode...';
        const response = await executeReapplyRequest();
        if (!response) {
          voucherDebugWarn('Cannot reapply: No SKU available');
          stopLoading();
          handleReapplyRetry(retryCount, 'waiting for SKU');
          return;
        }
        stopLoading();
        await processReapplyResponse(response);
      } catch (error) {
        handleReapplyError(error, retryCount);
      }
    };

    // Debounce timer for reapply
    let reapplyTimer = null;

    // Helper to get product info (used in reapply)
    const getProductInfo = () => {
      const productInfo = getProductInfoHelper();
      voucherDebug('getProductInfo called:', {
        sku: productInfo.sku,
        isCash: chosenChannel.value?.channelKey === 'Cash',
        channelKey: productInfo.channelKey,
        installmentPeriod: productInfo.installmentPeriod,
        chosenChannelKey: chosenChannel.value?.channelKey,
        chosenInstallmentMonths: chosenInstallment.value?.months
      });
      return productInfo;
    };

    const verifyCashPriceIfNeeded = async () => {
      const isCash = chosenChannel.value?.channelKey === 'Cash';
      if (!isCash) {
        return;
      }
      const cart = getCart.value;
      const deviceItem = findDeviceItemInCart(cart);
      const lineItemPrice = deviceItem?.cartProduct?.selectedPrice?.lineItemPrice;
      const expectedCashPrice = chosenChannel.value?.productBundlePrice?.cashPrice;
      if (lineItemPrice !== expectedCashPrice && expectedCashPrice) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    };

    const executeDelayedReapply = async (reason = 'unknown') => {
      await nextTick();
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));
      ensureVoucherCodeFromCart();
      await verifyCashPriceIfNeeded();

      if (!canReapplyVoucher()) {
        voucherDebug('Cannot reapply after delay - product info not ready');
        return;
      }

      voucherDebug('Auto-reapplying voucher code due to variant change', {
        isCash: chosenChannel.value?.channelKey === 'Cash',
        channelKey: chosenChannel.value?.channelKey,
        reason
      });
      reapplyVoucher(0, reason);
    };

    // Check if voucher code is available for reapply
    const hasVoucherCodeForReapply = () => {
      return voucherCode.value || isApplied.value;
    };

    // Ensure voucher code from cart if needed
    const ensureVoucherCodeIfApplied = () => {
      if (isApplied.value && !voucherCode.value) {
        ensureVoucherCodeFromCart();
      }
    };

    // Check if reapply should proceed
    const shouldProceedWithReapply = () => {
      if (!hasVoucherCodeForReapply()) {
        voucherDebug('Skipping reapply: no voucher code entered in field');
        return false;
      }
      ensureVoucherCodeIfApplied();
      return canReapplyVoucher();
    };

    const debouncedReapply = (delay = 300, reason = 'unknown') => {
      if (!shouldProceedWithReapply()) {
        return;
      }

      if (reapplyTimer) {
        clearTimeout(reapplyTimer);
      }
      // Store reason for tracking
      reapplyTimer = setTimeout(() => executeDelayedReapply(reason), delay);
    };

    const updatePricesIfVoucherApplied = async () => {
      if (isApplied.value && getCart.value) {
        await updateInstallmentOptionsFromCart(getCart.value);
      }
    }

    const getSubscriptionChannelId = (channel) => {
      if (channel.channelReferenceID) {
        return channel.channelReferenceID;
      }
      if (channel.channelKey) {
        return channel.channelKey;
      }
      return channel.subscription?.bundleName || null;
    };

    const getChannelId = (channel) => {
      if (!channel) {
        return null;
      }
      if (channel.channelKey === 'Cash') {
        return 'Cash';
      }
      return getSubscriptionChannelId(channel);
    };

    const hasChannelChanged = (newChannel, oldChannel) => {
      if (!newChannel || !oldChannel) {
        return false;
      }
      const newChannelId = getChannelId(newChannel);
      const oldChannelId = getChannelId(oldChannel);
      const changed = newChannelId !== oldChannelId;
      if (changed) {
        voucherDebug('Channel ID changed:', {
          old: { id: oldChannelId, channelKey: oldChannel?.channelKey, channelReferenceID: oldChannel?.channelReferenceID },
          new: { id: newChannelId, channelKey: newChannel?.channelKey, channelReferenceID: newChannel?.channelReferenceID }
        });
      }
      return changed;
    }

    const ensureVoucherCodeFromCart = () => {
      // If voucher code is not set locally but exists in cart, restore it using existing function
      // CRITICAL: Only restore if cart contains current product
      const cart = getCart.value;
      if (!voucherCode.value && cart?.appliedDiscountCodes && cart.appliedDiscountCodes.length > 0) {
        if (!cartContainsCurrentProduct(cart)) {
          voucherDebug('Cart has voucher codes but not for current product, skipping ensureVoucherCodeFromCart');
          return false;
        }
        const cartCode = cart.appliedDiscountCodes[0];
        return restoreCodeFromCartCode(cartCode);
      }
      return false;
    }

    // Wait for channel to be updated
    const waitForChannelUpdate = async (newChannel) => {
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 200));

      // Verify channel is updated
      if (chosenChannel.value?.channelKey !== newChannel?.channelKey) {
        voucherDebug('Channel mismatch detected, waiting more...');
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    };

    // Restore voucher code from cart if needed
    const restoreVoucherCodeIfApplied = () => {
      if (isApplied.value && !voucherCode.value) {
        ensureVoucherCodeFromCart();
      }
    };

    // Check if voucher code is available for reapply
    const hasVoucherCodeForChannelReapply = () => {
      if (!voucherCode.value && !isApplied.value) {
        voucherDebug('No voucher code entered, skipping reapply on channel change');
        return false;
      }
      return hasVoucherApplied();
    };

    // Check if voucher should be reapplied on channel change
    const shouldReapplyOnChannelChange = () => {
      restoreVoucherCodeIfApplied();
      return hasVoucherCodeForChannelReapply();
    };

    const processChannelChange = async (newChannel, oldChannel) => {
      await waitForChannelUpdate(newChannel);

      if (!shouldReapplyOnChannelChange()) {
        return;
      }

      voucherDebug('Channel changed, will reapply voucher:', newChannel?.channelKey, 'from', oldChannel?.channelKey);
      storeOriginalInstallmentOptions();
      if (!isComingBackFromCheckout()) {
        await resetInstallmentOptionsToOriginal();
      }
      await updatePricesIfVoucherApplied();
      debouncedReapply(800, 'channel change');
    }

    const handleChannelChange = async (newChannel, oldChannel) => {
      voucherDebug('handleChannelChange called:', {
        newChannelKey: newChannel?.channelKey,
        oldChannelKey: oldChannel?.channelKey,
        hasVoucher: hasVoucherApplied()
      });
      // Always process if this is the first channel selection
      if (!oldChannel && newChannel) {
        voucherDebug('Initial channel selection, will reapply voucher if present:', newChannel?.channelKey);
        await processChannelChange(newChannel, oldChannel);
        return;
      }
      // Check if channel actually changed
      if (!hasChannelChanged(newChannel, oldChannel)) {
        voucherDebug('Channel not changed, skipping reapply');
        return;
      }
      await processChannelChange(newChannel, oldChannel);
    }

    const areInstallmentsInvalid = (newInstallment, oldInstallment) => {
      return !newInstallment || !oldInstallment || newInstallment === oldInstallment;
    };

    const hasInstallmentPeriodChanged = (newInstallment, oldInstallment) => {
      if (!newInstallment || !oldInstallment) {
        return false;
      }
      return newInstallment.months !== oldInstallment.months;
    };

    const shouldSkipInstallmentReapply = (newInstallment, oldInstallment) => {
      if (isUpdatingFromCart.value) {
        return true;
      }
      if (areInstallmentsInvalid(newInstallment, oldInstallment)) {
        return true;
      }
      if (!hasInstallmentPeriodChanged(newInstallment, oldInstallment)) {
        voucherDebug('Installment period unchanged, skipping reapply (only prices changed)');
        return true;
      }
      return false;
    };

    const handleInstallmentChange = (newInstallment, oldInstallment) => {
      if (shouldSkipInstallmentReapply(newInstallment, oldInstallment)) {
        return;
      }
      // CRITICAL: Only reapply if voucher code is actually entered/applied
      if (!voucherCode.value && !isApplied.value) {
        voucherDebug('Installment changed but no voucher code entered, skipping reapply');
        return;
      }
      voucherDebug('Installment changed, will reapply voucher:', newInstallment?.months);
      updatePricesIfVoucherApplied();
      debouncedReapply(300, 'installment change');
    };

    // Watch for changes in subscription/channel selection
    // Use a computed property to get a stable channel identifier for reliable change detection
    const currentChannelId = computed(() => getChannelId(chosenChannel.value));

    const hasVoucherApplied = () => {
      const cartHasDiscountCodes = getCart.value?.appliedDiscountCodes && getCart.value.appliedDiscountCodes.length > 0;
      return hasAppliedDiscountCode.value || isApplied.value || !!voucherCode.value || cartHasDiscountCodes;
    }

    const handleChannelIdChange = (newId, oldId) => {
      voucherDebug('Channel ID watcher triggered:', {
        oldId,
        newId,
        hasVoucher: hasVoucherApplied(),
        voucherCode: voucherCode.value,
        isApplied: isApplied.value,
        hasAppliedDiscountCode: hasAppliedDiscountCode.value
      });
      if (!oldId || newId === oldId) {
        voucherDebug('Skipping channel ID change - no change detected or initial load');
        return;
      }
      if (!hasVoucherApplied()) {
        voucherDebug('Skipping channel ID change - no voucher applied');
        return;
      }
      voucherDebug('Channel ID changed from', oldId, 'to', newId, '- triggering reapply');
      const newChannel = chosenChannel.value;
      const oldChannel = oldId === 'Cash' ? { channelKey: 'Cash' } : { channelReferenceID: oldId };
      handleChannelChange(newChannel, oldChannel);
    }

    watch(currentChannelId, handleChannelIdChange);

    watch(chosenChannel, (newChannel, oldChannel) => {
      voucherDebug('chosenChannel watcher triggered:', {
        newChannelKey: newChannel?.channelKey,
        oldChannelKey: oldChannel?.channelKey,
        newChannelRefId: newChannel?.channelReferenceID,
        oldChannelRefId: oldChannel?.channelReferenceID,
        hasVoucher: hasVoucherApplied(),
        voucherCode: voucherCode.value,
        isApplied: isApplied.value,
        hasAppliedDiscountCode: hasAppliedDiscountCode.value
      });
      // Store original prices when channel is first loaded
      if (newChannel && !oldChannel && newChannel.installmentOptions) {
        storeOriginalInstallmentOptions();
      }
      // Handle channel changes
      handleChannelChange(newChannel, oldChannel);
    }, { deep: true });

    // Watch for changes in installment selection
    // The isUpdatingFromCart flag prevents triggering reapply during programmatic updates
    watch(chosenInstallment, handleInstallmentChange, { deep: true });

    const isInitialLoad = (oldOptions, newOptions) => {
      return !oldOptions && newOptions;
    };

    const hasOptionsChanged = (newOptions, oldOptions) => {
      return newOptions && oldOptions && newOptions !== oldOptions;
    };

    const hasValidChosenInstallment = () => {
      return chosenInstallment.value?.months !== undefined;
    };

    // Watch for changes in installmentOptions to ensure chosenInstallment is updated
    const shouldUpdateFromOptionsChange = (newOptions, oldOptions) => {
      if (isUpdatingFromCart.value) {
        return false;
      }
      const isInitial = isInitialLoad(oldOptions, newOptions);
      const hasChanged = hasOptionsChanged(newOptions, oldOptions);
      const isValid = hasValidChosenInstallment();
      return (isInitial || hasChanged) && isValid;
    };

    const handleInstallmentOptionsChange = (newOptions, oldOptions) => {
      if (!shouldUpdateFromOptionsChange(newOptions, oldOptions)) {
        return;
      }
      if (!hasVoucherApplied()) {
        applyDefaultPdpInstallmentSelection();
        return;
      }
      voucherDebug('installmentOptions changed, updating chosenInstallment:', {
        currentMonths: chosenInstallment.value?.months,
        optionsChanged: true,
        isInitialLoad: !oldOptions
      });
      updateChosenInstallmentFromOptions();
    };

    watch(() => chosenChannel.value?.installmentOptions, handleInstallmentOptionsChange, { deep: true });

    // Check if variant change should trigger reapply
    const shouldReapplyOnVariantChange = (type) => {
      if (!voucherCode.value && !isApplied.value) {
        voucherDebug(`${type} changed but no voucher code entered, skipping reapply`);
        return false;
      }
      return true;
    };

    // Handle variant changes (storage, color, SKU)
    const handleVariantChange = (type, oldValue, newValue, delay = 800) => {
      if (!newValue || !oldValue || newValue === oldValue) {
        return;
      }

      if (!shouldReapplyOnVariantChange(type)) {
        return;
      }

      voucherDebug(`${type} changed, will reapply voucher:`, oldValue, '->', newValue);
      errorMessage.value = '';
      const reason = `${type.toLowerCase()} change`;
      debouncedReapply(delay, reason);
    };

    watch(chosenStorage, (newStorage, oldStorage) => handleVariantChange('Storage', oldStorage, newStorage));
    watch(chosenColor, (newColor, oldColor) => handleVariantChange('Color', oldColor, newColor));
    watch(() => productDetails.value?.chosenVariantSku, (newSku, oldSku) => handleVariantChange('SKU', oldSku, newSku, 600));

    // Watch for product changes (different product, not just variant)
    // When product changes, clear discount codes as they are product-specific
    // Watch both productName and route params to detect product changes
    const productKey = computed(() => {
      return route.value?.params?.productslug || productDetails.value?.productName || '';
    });

    // Empty cart when navigating to new product
    const emptyCartOnProductChange = async () => {
      const cartID = getCartID();
      if (!cartID) {
        return;
      }

      try {
        voucherDebug('Emptying cart when navigating to new product');
        await $hubble.cart.emptyCart(cartID);
        // Wait a bit to ensure cart is emptied before setting to null
        await nextTick();
      } catch (error) {
        voucherDebugError('Error emptying cart on product change:', error);
      }
    };

    // Reset everything when navigating between products
    const resetEverythingOnProductChange = async () => {
      // CRITICAL: Set flag to prevent all voucher restoration during product change
      isChangingProduct.value = true;

      // CRITICAL: Clear voucher state FIRST before emptying cart
      // This prevents cart watcher from restoring voucher codes during the reset process
      // Also clear enteredVoucherCode to prevent it from being restored later
      appliedDiscountCodeStore.clearDiscountCodes();
      appliedDiscountCodeStore.setEnteredVoucherCode(''); // CRITICAL: Clear stored voucher code
      resetVoucherState();

      await emptyCartOnProductChange();

      // CRITICAL: Set cart to null AFTER emptying to prevent watcher from restoring old voucher codes
      cartStore.setCart(null);
      await nextTick(); // Ensure cart watcher has processed the null cart
      await resetInstallmentOptionsToOriginal();

      // CRITICAL: Reset flag after a delay to allow all watchers to process the changes
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));
      isChangingProduct.value = false;
    };

    watch(productKey, async (newKey, oldKey) => {
      if (newKey && oldKey && newKey !== oldKey) {
        voucherDebug('Product changed (route or name), resetting everything:', oldKey, '->', newKey);
        // CRITICAL: Clear enteredVoucherCode immediately when product changes
        // This prevents it from being restored when navigating back
        appliedDiscountCodeStore.setEnteredVoucherCode('');
        await resetEverythingOnProductChange();
      }
    });

    // Use composable method
    const cartContainsCurrentProduct = (cart) => voucherCartState.cartContainsCurrentProduct(cart);

    // Sync store with cart using composable
    const syncStoreWithCart = async (isPageLoad = false) => {
      await voucherCartState.syncStoreWithCart(
        isPageLoad,
        isDeviceOrAccessory.value,
        () => isApplied.value || !!voucherCode.value,
        resetVoucherState,
        resetInstallmentOptionsToOriginal,
        isComingBackFromCheckout
      )
    };

    const hasCodes = (codes) => Array.isArray(codes) && codes.length > 0;
    const codesWereEmpty = (oldCodes) => !oldCodes || oldCodes.length === 0;
    const fieldIsEmpty = () => !voucherCode.value && !isApplied.value;
    const shouldRestoreFromCodesWatch = (newCodes, oldCodes) =>
      hasCodes(newCodes) && codesWereEmpty(oldCodes) && fieldIsEmpty();

    const shouldSkipAppliedDiscountCodesWatch = () => {
      return suppressVoucherRestoreFromCart.value || isChangingProduct.value;
    };

    const restoreVoucherFromCodesWatchIfNeeded = () => {
      const cart = getCart.value;
      if (cart && cartContainsCurrentProduct(cart)) {
        restoreVoucherCodeFromCart();
        return;
      }
      voucherDebug('Applied discount codes changed but cart does not contain current product, skipping restore');
      appliedDiscountCodeStore.setEnteredVoucherCode('');
    };

    const handleAppliedDiscountCodesChange = (newCodes, oldCodes) => {
      if (shouldSkipAppliedDiscountCodesWatch()) {
        return;
      }
      if (!shouldRestoreFromCodesWatch(newCodes, oldCodes)) {
        return;
      }
      restoreVoucherFromCodesWatchIfNeeded();
    };

    // Watch for applied discount codes to restore voucher code when navigating back
    watch(() => appliedDiscountCodeStore.appliedDiscountCodes, handleAppliedDiscountCodesChange, { deep: true });

    const hasRestorableDiscountCodes = (newCart) => {
      return !!(newCart?.appliedDiscountCodes?.length) && !voucherCode.value && !isApplied.value;
    };

    const isFlowAllowedForRestore = (newCart) => {
      // For regular device/accessory flows we skip automatic restore here,
      // but for keep-subscription carts we must allow restore so the voucher
      // code is preserved and can be reapplied together with Mobilerabat.
      const isBlockedDeviceFlow = isDeviceOrAccessory.value && !isKeepSubscriptionCart.value;
      return !isBlockedDeviceFlow && cartContainsCurrentProduct(newCart);
    };

    const canRestoreVoucherFromCart = (newCart) => {
      return hasRestorableDiscountCodes(newCart) && isFlowAllowedForRestore(newCart);
    };

    const reapplyAfterRestore = async () => {
      if (!isComingBackFromCheckout() || !isApplied.value || !voucherCode.value) {
        return;
      }
      voucherDebug('Coming back from checkout, reapplying voucher code:', voucherCode.value);
      await nextTick();
      debouncedReapply(300, 'restore from checkout');
    };

    // Handle voucher restore from cart
    const handleVoucherRestore = async (newCart) => {
      if (!canRestoreVoucherFromCart(newCart)) {
        return;
      }
      await nextTick();
      await restoreVoucherCodeFromCart();
      await reapplyAfterRestore();
    };

    // Check if cart has voucher codes for a different product
    const shouldSkipCartSync = (newCart) => {
      if (!newCart?.appliedDiscountCodes?.length) {
        return false;
      }
      const cartHasCurrentProduct = cartContainsCurrentProduct(newCart);
      if (!cartHasCurrentProduct) {
        voucherDebug('Cart has voucher codes but not for current product, skipping restore');
        return true;
      }
      return false;
    };

    // Check if cart change should be skipped
    const shouldSkipCartChange = (newCart) => {
      if (suppressVoucherRestoreFromCart.value) {
        return true;
      }
      // Skip syncing discount code store if we're in the middle of reapplying voucher
      if (isReapplyingVoucher.value) {
        return true;
      }

      // CRITICAL: Skip all voucher restoration during product change
      if (isChangingProduct.value) {
        voucherDebug('Skipping cart sync during product change');
        return true;
      }

      // CRITICAL: Don't restore voucher codes if cart doesn't contain current product
      return shouldSkipCartSync(newCart);
    };

    const handleCartChange = async (newCart, oldCart) => {
      if (shouldSkipCartChange(newCart)) {
        return;
      }

      if (newCart && newCart !== oldCart) {
        await syncStoreWithCart(false);
      }
      await handleVoucherRestore(newCart);
    };

    // Watch cart to restore voucher code when it changes or is loaded
    watch(getCart, handleCartChange, { deep: true, immediate: true });

    const shouldKeepPanelOpen = (newValue, oldValue) => {
      return isApplied.value && oldValue && oldValue.length > 0 && (!newValue || newValue.length === 0);
    };

    // Watch panel value to prevent closing when voucher is applied
    watch(panel, (newValue, oldValue) => {
      if (shouldKeepPanelOpen(newValue, oldValue)) {
        nextTick(() => {
          if (isApplied.value) {
            panel.value = [0];
          }
        });
      }
    });

    const getCartDiscountCodeId = (cart) => {
      return cart?.appliedDiscountCodes?.[0]?.id;
    };

    const cartHasAppliedDiscountCodes = (cart) => {
      return !!(cart?.appliedDiscountCodes && cart.appliedDiscountCodes.length > 0);
    };

    const syncCartInstallmentsAfterVoucherRemoved = async (updatedCart) => {
      if (!updatedCart) {
        cartStore.setCart(updatedCart);
        return;
      }
      const cartForStore = JSON.parse(JSON.stringify(updatedCart));
      updateCartInstallmentFromChosenChannel(cartForStore);
      cartStore.setCart(cartForStore);
      await nextTick();
      updateChosenInstallmentFromOptions();
    };

    const handleSilentRemoveSuccess = async (updatedCart) => {
      appliedDiscountCodeStore.clearDiscountCodes();
      if (updatedCart?.totalDiscountCodeDiscount !== undefined) {
        appliedDiscountCodeStore.setTotalDiscountCodeDiscount(updatedCart.totalDiscountCodeDiscount);
      }
      await resetInstallmentOptionsToOriginal();
      await syncCartInstallmentsAfterVoucherRemoved(updatedCart);
      appliedDiscountCodeStore.setEnteredVoucherCode('');
      resetVoucherState();
    };

    const removeDiscountCodesFromCartIfPresent = async () => {
      const cart = getCart.value;
      const cartID = getCartID();
      const discountCodeId = getCartDiscountCodeId(cart);
      if (!cartID || !discountCodeId || !cartHasAppliedDiscountCodes(cart)) {
        return;
      }
      await voucherApp.removeVoucher(
        discountCodeId,
        handleSilentRemoveSuccess,
        (message) => voucherDebugError('PDP voucher reset: could not remove discount code from cart:', message)
      );
    };

    const resetPdpVoucherOnEntry = async () => {
      appliedDiscountCodeStore.clearDiscountCodes();
      appliedDiscountCodeStore.setEnteredVoucherCode('');
      resetVoucherState();
      await removeDiscountCodesFromCartIfPresent();
      voucherDebug('PDP voucher reset: cleared local state and removed cart discount codes if present');
    };

    const isKontantInstallmentOption = (option) => {
      return option?.months === 0 || option?.displayText === 'Kontant';
    };

    const findInstallmentByDefaultMonths = (matrix, defaultMonths) => {
      if (defaultMonths === null || defaultMonths === undefined) {
        return null;
      }
      return matrix.find(opt => opt.months === defaultMonths) || null;
    };

    const buildSyntheticKontantOption = (productWithSubPrice) => ({
      months: 0,
      amount: productWithSubPrice,
      displayText: 'Kontant',
      monthlyAmountWithSubscription: productWithSubPrice
    });

    const findKontantOrSyntheticOption = (matrix, productWithSubPrice) => {
      const kontantOption = matrix.find(isKontantInstallmentOption);
      if (kontantOption) {
        return kontantOption;
      }
      if (productWithSubPrice !== undefined && productWithSubPrice !== null) {
        return buildSyntheticKontantOption(productWithSubPrice);
      }
      return null;
    };

    const findDefaultPdpInstallmentOption = () => {
      const matrix = chosenChannel.value.installmentOptions || [];
      const productWithSubPrice = chosenChannel.value.productBundlePrice?.productWithSubscriptionPrice;
      const kontantOption = findKontantOrSyntheticOption(matrix, productWithSubPrice);
      if (kontantOption) {
        return kontantOption;
      }
      const defaultMonths = productDetails.value?.defaultInstallmentPeriod;
      return findInstallmentByDefaultMonths(matrix, defaultMonths);
    };

    const getMonthlyAmountForInstallmentOption = (option) => {
      if (option.months === 0) {
        return option.amount;
      }
      return option.monthlyAmountWithSubscription ?? option.amount;
    };

    const setChosenInstallmentFromOption = (option) => {
      chosenInstallment.value = {
        months: option.months,
        amount: option.amount,
        displayText: option.displayText,
        monthlyAmountWithSubscription: getMonthlyAmountForInstallmentOption(option)
      };
      voucherDebug('PDP entry: applied default installment selection', {
        months: option.months,
        displayText: option.displayText
      });
    };

    const applyDefaultPdpInstallmentSelection = () => {
      if (!chosenChannel.value) {
        return;
      }
      const option = findDefaultPdpInstallmentOption();
      if (!option) {
        return;
      }
      setChosenInstallmentFromOption(option);
    };

    // CRITICAL: Reset everything on mount/reload/load for ALL products
    // This ensures no voucher or price data is shared between products
    onMounted(async () => {
      voucherDebug('onMounted: Completely resetting ALL voucher and price data for new product');

      // CRITICAL: Set flags to prevent cart watchers and restoration during reset
      isUpdatingFromCart.value = true;
      isChangingProduct.value = true;

      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));

      // Fresh PDP visit: never restore voucher from cart or store
      await resetPdpVoucherOnEntry();

      // Wait for InstallmentsOptions to reset chosenChannel
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 50));

      // CRITICAL: Wait for productDetails to be updated, then reset prices to original
      await resetInstallmentOptionsToOriginal();
      voucherDebug('onMounted: Reset installment options to original prices');

      // Store original prices when product/channel is first loaded (after reset)
      storeOriginalInstallmentOptions();
      voucherDebug('onMounted: Stored fresh original prices for new product');

      applyDefaultPdpInstallmentSelection();
      voucherDebug('onMounted: Applied default Kontant installment for PDP entry');

      // CRITICAL: Reset flags after reset is complete to allow cart updates
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));
      isUpdatingFromCart.value = false;
      isChangingProduct.value = false;
      suppressVoucherRestoreFromCart.value = false;
    });

    onBeforeUnmount(() => {
      suppressVoucherRestoreFromCart.value = true;
      appliedDiscountCodeStore.clearDiscountCodes();
      appliedDiscountCodeStore.setEnteredVoucherCode('');
    });

    const handleRemoveSuccess = async (updatedCart, _code) => {
      // Track voucher removal before clearing state
      const codeToTrack = voucherCode.value || appliedDiscountCodeStore.appliedDiscountCodes?.[0]?.code || 'unknown';
      trackRemoveVoucher({
        voucherCode: codeToTrack
      });

      // Clear voucher store before resetting installments so cart price helpers
      // do not still apply the removed discount during recalculation.
      appliedDiscountCodeStore.clearDiscountCodes();
      if (updatedCart?.totalDiscountCodeDiscount !== undefined) {
        appliedDiscountCodeStore.setTotalDiscountCodeDiscount(updatedCart.totalDiscountCodeDiscount);
      }

      await resetInstallmentOptionsToOriginal();
      await syncCartInstallmentsAfterVoucherRemoved(updatedCart);
      appliedDiscountCodeStore.setEnteredVoucherCode('');
      resetVoucherState();

      emit('applied', { code: null, cart: updatedCart, removed: true });
    };

    // Remove voucher using composable
    const removeVoucher = async () => {
      const cartID = getCartID();
      if (!cartID) {
        handleError('Ingen indkøbskurv fundet');
        return;
      }

      const appliedCodes = appliedDiscountCodeStore.appliedDiscountCodes;
      const discountCodeId = appliedCodes?.[0]?.id || getCart.value?.appliedDiscountCodes?.[0]?.id;

      if (!discountCodeId) {
        handleError('Ingen rabatkode at fjerne');
        return;
      }

      await voucherApp.removeVoucher(
        discountCodeId,
        handleRemoveSuccess,
        handleError
      );
    };

    // Expose reapplyVoucher for parent component to call on color/storage changes
    // Note: In Vue 2 Composition API, we use the return object instead of expose

    return {
      voucherCode,
      errorMessage,
      infoMessage,
      loading,
      removing,
      isApplied,
      panel,
      applyVoucher,
      removeVoucher,
      clearError,
      reapplyVoucher,
      showDiscountExceedsPriceWarning
    };
  }
};
